<template>
  <div class="remittance-container">
    <page-header :title="$t('remittances.listRemittance.title')" />
    <div class="spacing-24"></div>
    <div v-if="pageLoading">
      <loader :height="400" />
    </div>
    <div v-else-if="paymentList && paymentList.length">
      <b-row class="ml-0">
        <b-col md="8">
          <div class="remittance-list-container">
            <list-row-widget
              id="remittance-list"
              v-for="(remittance, i) in paymentList"
              :key="i"
              :data="remittance"
              :buttonText="$t('remittances.listRemittance.listCardButton')"
              :redirectTo="redirectToView"
            >
              <!-- Title section -->
              <template v-slot:title>
                <div class="truncate-text title-width mr-2">
                  {{
                    (remittance.importer_user &&
                      remittance.importer_user.company) | capitalize
                  }}
                </div>
                <div class="remittance-status">
                  <chip
                    :class="['ml-0', getAccountStatus(remittance)]"
                    :chipText="getAccountStatus(remittance)"
                  />
                </div>
              </template>
              <template v-slot:subtitle>
                <div
                  v-if="remittance.remitter_name"
                  class="drip-body-small color-noble-blue-500 w-100 truncate_text"
                >
                  {{ remittance.remitter_name | capitalize }}
                </div>
                <div class="spacing-4"></div>
              </template>
              <template v-slot:body>
                <div class="mr-2 balance-text">
                  {{ $t('remittances.listRemittance.listCardInvoiceSelected') }}
                  {{
                    (remittance.linked_invoices &&
                      remittance.linked_invoices.length) ||
                    0
                  }}{{ remittance.unfinanced_amount ? ', Un-financed' : '' }}
                </div>
                <span class="color-sky-blue-500">|</span>
                <div class="mx-2 truncate-text">
                  {{ $t('remittances.listRemittance.listCardRemittanceAmt') }}
                  {{
                    getCurrencySymbol(
                      remittance.currency && remittance.currency.toUpperCase()
                    )
                  }}
                  {{
                    remittance.remitted_amount &&
                    convertToCurrency(remittance.remitted_amount)
                  }}
                </div>
                <span class="color-sky-blue-500">|</span>
                <div class="ml-2">
                  {{ remittance.currency && remittance.currency.toUpperCase() }}
                </div>
              </template>
            </list-row-widget>
            <b-pagination
              v-if="totalRows"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              size="sm"
              align="left"
              class="mt-3 bg-white"
              aria-controls="remittance-list"
            >
              <template #first-text="{ disabled }">
                <img
                  v-if="disabled"
                  src="@/assets/images/table/first-page-disabled.svg"
                  alt="first-page"
                />
                <img
                  v-else
                  src="@/assets/images/table/first-page.svg"
                  alt="first-page"
                />
              </template>
              <template #last-text="{ disabled }">
                <img
                  v-if="disabled"
                  src="@/assets/images/table/last-page-disabled.svg"
                  alt="last-page"
                />
                <img
                  v-else
                  src="@/assets/images/table/last-page.svg"
                  alt="last-page"
                />
              </template>
              <template #prev-text="{ disabled }">
                <img
                  v-if="disabled"
                  src="@/assets/images/table/prev-page-disabled.svg"
                  alt="prev-page"
                />
                <img
                  v-else
                  src="@/assets/images/table/prev-page.svg"
                  alt="prev-page"
                />
              </template>
              <template #next-text="{ disabled }">
                <img
                  v-if="disabled"
                  src="@/assets/images/table/next-page-disabled.svg"
                  alt="next-page"
                />
                <img
                  v-else
                  src="@/assets/images/table/next-page.svg"
                  alt="next-page"
                />
              </template>
            </b-pagination>
          </div>
        </b-col>
        <b-col md="4">
          <add-more-widget
            :heading="$t('remittances.listRemittance.cardHeading')"
            :subtitle="$t('remittances.listRemittance.cardDesc')"
            :buttonText="$t('remittances.listRemittance.cardButton')"
            :buttonClick="redirectToRemittance"
            :sideWidget="true"
          />
        </b-col>
      </b-row>
    </div>
    <b-row v-else>
      <b-col md="12">
        <add-more-widget
          :heading="$t('remittances.listRemittance.cardHeading2')"
          :title="$t('remittances.listRemittance.cardTitle')"
          :subtitle="$t('remittances.listRemittance.cardDesc2')"
          :buttonText="$t('remittances.listRemittance.cardButton2')"
          :buttonClick="redirectToRemittance"
          :border="true"
          :sideWidget="false"
        />
      </b-col>
    </b-row>
  </div>
</template>
<script>
import remittanceService from '@/services/remittance'
import Chip from '@/components/common/Chip'
import Loader from '@/components/common/Loader'
import PageHeader from '@/components/common/PageHeader'
import AddMoreWidget from '@/components/common/AddMoreWidget'
import ListRowWidget from '@/components/common/ListRowWidget'
import inputMasking from '@/helpers/input-masking'
import { CURRENCY_SYMBOL } from '@/utils/constants'

export default {
  errorCaptured: function (err) {
    console.log('Caught error', err.message)
  },
  components: {
    PageHeader,
    Chip,
    Loader,
    AddMoreWidget,
    ListRowWidget
  },
  data() {
    return {
      currentPage: 1,
      perPage: 0,
      totalRows: 0,
      paymentList: [],
      pageLoading: true
    }
  },
  mounted() {
    const limit = 10
    this.getRemittanceList(limit, this.currentPage)
    this.$mpTrack('Add Remittance - Remittance list opened')
  },
  methods: {
    getCurrencySymbol(curr) {
      if (curr) {
        return CURRENCY_SYMBOL[curr] || ''
      }
      return ''
    },
    convertToCurrency(num) {
      if (num) {
        return inputMasking.convertToCurrency(num) || ''
      }
      return 0
    },
    redirectToView(data) {
      this.$mpTrack(this.$t('remittances.listRemittance.eventView'), {
        remittance_id: data.id
      })
      this.$router.push({
        name: 'viewRemittance',
        params: {
          rid: data.id
        }
      })
    },
    redirectToRemittance() {
      this.$mpTrack(this.$t('remittances.listRemittance.eventAdd'))
      this.$router.push({
        name: 'addRemittance',
        params: {}
      })
    },
    getRemittanceList(limit, page) {
      remittanceService
        .fetchRemittanceList(this.$store.state.account.accountId, limit, page)
        .then((res) => {
          this.totalRows = res.data.meta.pagination.total_objects
          this.perPage = res.data.meta.pagination.per_page
          this.paymentList = res.data['drip/business/remittances']
          this.pageLoading = false
        })
        .catch((e) => {
          this.pageLoading = false
          // this.listLoading = false
          console.error(e)
        })
    },
    getAccountStatus({ status }) {
      if (status === 'Pending') {
        return this.$t('remittances.listRemittance.listCardStatus.pending')
      }
      if (status === 'Partial Marked') {
        return this.$t('remittances.listRemittance.listCardStatus.partial')
      }
      if (status === 'Marked') {
        return this.$t('remittances.listRemittance.listCardStatus.marked')
      }
      return ''
    }
  },
  watch: {
    currentPage(currPage) {
      if (currPage) {
        this.getRemittanceList(10, currPage)
      }
    }
  }
}
</script>
<style lang="scss">
.arrow::before {
  border-top-color: $neutral-gray-100 !important;
}
.page-item.disabled .page-link {
  border-color: white;
  color: grey;
  height: 32px;
  .prev-page-svg {
    color: red;
  }
}
.page-item.active .page-link {
  border-radius: 8px;
}
.page-link {
  height: 32px;
  border-color: white;
  &:hover {
    border-radius: 8px;
  }
}
</style>
<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
.remittance-container {
  padding: 40px 108px;
  margin-bottom: 8px;
  @media screen and (max-width: 1200px) {
    padding: 40px 56px;
  }
  @media screen and (max-width: 768px) {
    padding: 40px 24px;
  }
  .remittance-list-container {
    .bank-account {
      padding: 24px 16px;
      border: 1px solid $sky-blue-400;
      border-radius: 8px;
      .bank-status {
        &.Verified {
          border: 1px solid $spring-green-400;
          background: $spring-green-50;
          color: $noble-blue-500;
        }
        &.Pending {
          border: 1px solid $mustard-yellow-400;
          background: $mustard-yellow-50;
          color: $noble-blue-500;
        }
      }
    }
  }
}
</style>
